/**
 * Derived-channel engine.
 *
 * The DJI SRT stores position and altitude but NOT speed, heading, vertical
 * speed or distance. Those are computed here, once, over the raw series — and
 * every lens reads them like any other channel.
 *
 * The non-obvious part: GPS updates at ~10 Hz while the file has one sample per
 * video frame (~60 Hz), so ~6 of every 7 samples carry an identical coordinate.
 * Differencing adjacent samples therefore yields zero for most frames. We
 * instead difference across a fixed *time window* (which always spans several
 * real GPS fixes) and then median-filter to drop single-fix glitches (one such
 * glitch in real data implied 163 m/s).
 */

import { haversine, bearing, toENU, gpsValid } from '../geo/geo.ts';
import { indexAtTime } from '../model/types.ts';
import type { ChannelDef, Flight } from '../model/types.ts';

/** Half-width of the differencing window for speed/heading (ms). */
const SPEED_WINDOW_MS = 1000;
/** Half-width of the median filter that removes GPS spikes (ms). */
const MEDIAN_WINDOW_MS = 600;
/**
 * Segments implying a speed above this are treated as GPS teleport glitches and
 * excluded from cumulative path length. 60 m/s (216 km/h) is well above any
 * consumer drone yet low enough to catch coordinate jumps.
 */
const MAX_PLAUSIBLE_SPEED = 60;
/**
 * A fix gap longer than this spans a GPS-loss stretch (normal fixes are ~0.1 s
 * apart). The drone may have drifted during it, but the path is unknowable, so we
 * don't add that distance — counting it at the single reacquisition frame would
 * fake a huge instantaneous speed.
 */
const MAX_FIX_GAP_MS = 2000;

interface DerivedSpec {
  key: string;
  label: string;
  unit: string;
  kind: ChannelDef['kind'];
}

/**
 * Add derived channels to a flight in place and return it. Requires latitude /
 * longitude to add motion channels; rel_alt to add vertical speed. Safe to call
 * on flights missing those — it simply adds fewer channels.
 */
export function deriveChannels(flight: Flight): Flight {
  const s = flight.samples;
  const n = s.length;
  const t = s.time;
  const lat = s.numeric['latitude'];
  const lon = s.numeric['longitude'];
  const rel = s.numeric['rel_alt'];

  const add = (spec: DerivedSpec, data: Float64Array) => {
    s.numeric[spec.key] = data;
    flight.channels.push({
      key: spec.key,
      label: spec.label,
      unit: spec.unit,
      kind: spec.kind,
      origin: 'derived',
      range: rangeOf(data),
    });
  };

  if (lat && lon) {
    // GPS validity per sample. DJI writes (0,0) when there's no fix (ATTI mode,
    // no satellites, signal loss). Position-derived channels are NaN on invalid
    // samples so they don't draw a line to null island or explode the distances —
    // while altitude (barometric) stays valid throughout.
    const valid = new Float64Array(n);
    for (let i = 0; i < n; i++) valid[i] = gpsValid(lat[i], lon[i]) ? 1 : 0;

    // Cumulative distance with per-segment glitch rejection. A bad inter-fix
    // segment (a GPS step or spike implying > MAX_PLAUSIBLE_SPEED) contributes
    // zero and the track re-anchors to the next sample — so both a returning
    // spike and a persistent step lose only that one segment. Speed is derived
    // from this same series, keeping speed and path length consistent.
    const cumDist = cleanCumulativeDistance(lat, lon, t, valid);

    const speed = new Float64Array(n); // m/s
    const course = new Float64Array(n); // deg
    const distHome = new Float64Array(n); // m
    const enuE = new Float64Array(n);
    const enuN = new Float64Array(n);

    for (let i = 0; i < n; i++) {
      const a = indexAtTime(flight, t[i] - SPEED_WINDOW_MS / 2);
      const b = indexAtTime(flight, t[i] + SPEED_WINDOW_MS / 2);
      const dt = (t[b] - t[a]) / 1000;
      speed[i] = dt > 0 ? (cumDist[b] - cumDist[a]) / dt : 0;
      // Heading from the window's net displacement; suppressed when the window
      // straddles a rejected segment (no clean distance accumulated).
      if (dt > 0 && valid[a] && valid[b] && cumDist[b] - cumDist[a] > 0.5) {
        course[i] = bearing(lat[a], lon[a], lat[b], lon[b]);
      } else {
        course[i] = i > 0 ? course[i - 1] : 0;
      }
      if (valid[i]) {
        distHome[i] = haversine(flight.origin.lat, flight.origin.lon, lat[i], lon[i]);
        const enu = toENU(lat[i], lon[i], flight.origin);
        enuE[i] = enu.e;
        enuN[i] = enu.n;
      } else {
        distHome[i] = NaN; // no fix → no meaningful position
        enuE[i] = NaN;
        enuN[i] = NaN;
      }
    }

    medianFilter(speed, t, MEDIAN_WINDOW_MS);

    add({ key: 'gps_valid', label: 'GPS fix', unit: '', kind: 'scalar' }, valid);
    add({ key: 'ground_speed', label: 'Ground speed', unit: 'm/s', kind: 'scalar' }, speed);
    add({ key: 'course', label: 'Course', unit: '°', kind: 'angle' }, course);
    add({ key: 'dist_from_home', label: 'Distance from home', unit: 'm', kind: 'scalar' }, distHome);
    add({ key: 'enu_e', label: 'East offset', unit: 'm', kind: 'scalar' }, enuE);
    add({ key: 'enu_n', label: 'North offset', unit: 'm', kind: 'scalar' }, enuN);

    add({ key: 'path_length', label: 'Path length', unit: 'm', kind: 'scalar' }, cumDist);
  }

  if (rel) {
    const vspeed = new Float64Array(n); // m/s, + = climbing
    for (let i = 0; i < n; i++) {
      const a = indexAtTime(flight, t[i] - SPEED_WINDOW_MS / 2);
      const b = indexAtTime(flight, t[i] + SPEED_WINDOW_MS / 2);
      const dt = (t[b] - t[a]) / 1000;
      vspeed[i] = dt > 0 ? (rel[b] - rel[a]) / dt : 0;
    }
    medianFilter(vspeed, t, MEDIAN_WINDOW_MS);
    add({ key: 'vertical_speed', label: 'Vertical speed', unit: 'm/s', kind: 'scalar' }, vspeed);
  }

  // Second-order channels, derived from the first-order ones above.
  const speed = s.numeric['ground_speed'];
  const course = s.numeric['course'];
  const vspeed = s.numeric['vertical_speed'];

  if (speed) {
    const accel = new Float64Array(n); // m/s^2
    for (let i = 0; i < n; i++) {
      const a = indexAtTime(flight, t[i] - SPEED_WINDOW_MS / 2);
      const b = indexAtTime(flight, t[i] + SPEED_WINDOW_MS / 2);
      const dt = (t[b] - t[a]) / 1000;
      accel[i] = dt > 0 ? (speed[b] - speed[a]) / dt : 0;
    }
    medianFilter(accel, t, MEDIAN_WINDOW_MS);
    add({ key: 'acceleration', label: 'Acceleration', unit: 'm/s²', kind: 'scalar' }, accel);
  }

  if (course) {
    const turn = new Float64Array(n); // deg/s (+ = turning right/clockwise)
    for (let i = 0; i < n; i++) {
      const a = indexAtTime(flight, t[i] - SPEED_WINDOW_MS / 2);
      const b = indexAtTime(flight, t[i] + SPEED_WINDOW_MS / 2);
      const dt = (t[b] - t[a]) / 1000;
      turn[i] = dt > 0 ? wrap180(course[b] - course[a]) / dt : 0;
    }
    medianFilter(turn, t, MEDIAN_WINDOW_MS);
    add({ key: 'turn_rate', label: 'Turn rate', unit: '°/s', kind: 'scalar' }, turn);
  }

  if (speed && vspeed) {
    const climb = new Float64Array(n); // deg, + = climbing
    for (let i = 0; i < n; i++) climb[i] = (Math.atan2(vspeed[i], Math.max(speed[i], 0.3)) * 180) / Math.PI;
    add({ key: 'climb_angle', label: 'Climb angle', unit: '°', kind: 'angle' }, climb);
  }

  return flight;
}

/** Wrap an angle difference to [-180, 180]. */
function wrap180(d: number): number {
  return ((((d + 180) % 360) + 360) % 360) - 180;
}

/**
 * Cumulative great-circle distance per sample (m), rejecting any inter-fix
 * segment whose implied speed exceeds MAX_PLAUSIBLE_SPEED. The rejected segment
 * adds zero distance, and because the next segment is measured from the current
 * sample, the track re-anchors immediately — so a single bad fix (spike) and a
 * sustained jump (step) both cost only one segment, not a sustained error.
 */
function cleanCumulativeDistance(
  lat: Float64Array,
  lon: Float64Array,
  t: Float64Array,
  valid: Float64Array,
): Float64Array {
  const n = lat.length;
  const out = new Float64Array(n);
  let acc = 0;
  // Walk distinct GPS fixes only: a coordinate update lands in a single frame,
  // so the segment's true duration is the time since the previous fix (~100 ms),
  // not the inter-sample gap (~16 ms). Measuring over the sample gap would make
  // ordinary motion look like a teleport. Invalid (0,0) fixes are skipped: they
  // never anchor and never add distance, so a GPS-loss stretch contributes nothing
  // and the track resumes cleanly from the next real fix.
  let prevFix = 0;
  while (prevFix < n && !valid[prevFix]) prevFix++;
  // Two anchors: `prevFix` is the last DISTINCT coordinate (a fix repeats across
  // ~6 frames, so distance/speed must be measured over the whole hold, not one
  // 16 ms frame); `lastValid` is the immediately-preceding valid sample. The gap
  // that signals GPS LOSS is the time since the last VALID sample — not the time
  // since the last distinct fix, which also grows during a legitimate hover.
  let lastValid = prevFix;
  for (let i = prevFix + 1; i < n; i++) {
    if (!valid[i]) {
      out[i] = acc;
      continue;
    }
    const isNewFix = lat[i] !== lat[prevFix] || lon[i] !== lon[prevFix];
    if (isNewFix) {
      const d = haversine(lat[prevFix], lon[prevFix], lat[i], lon[i]);
      const dtMs = t[i] - t[prevFix]; // over the fix hold → correct refresh-period speed
      const gapMs = t[i] - t[lastValid]; // since last valid sample → large only on real signal loss
      const dt = dtMs / 1000;
      // Reject reacquisition jumps across a GPS-loss gap; a long hover (gapMs tiny)
      // is legitimate and its first movement must still count.
      if (dt > 0 && gapMs <= MAX_FIX_GAP_MS && d / dt <= MAX_PLAUSIBLE_SPEED) acc += d;
      prevFix = i; // re-anchor on every distinct fix
    }
    lastValid = i; // advance on every valid sample
    out[i] = acc;
  }
  return out;
}

/**
 * In-place rolling median over a symmetric time window. Robust to single-sample
 * spikes (GPS glitches) in a way a moving average is not.
 *
 * Both window edges advance monotonically with i, so instead of re-sorting the
 * window every sample (O(n·w·log w) plus an allocation per step), we keep a
 * sorted multiset of the live window and splice one element in/out as each edge
 * moves — O(n·w), no per-step sort or allocation. Output is bit-identical to the
 * sort-the-window definition: median = sorted[size >> 1] (lower-middle).
 */
function medianFilter(values: Float64Array, t: Float64Array, halfWindowMs: number): void {
  const n = values.length;
  if (n === 0) return;
  const orig = Float64Array.from(values);
  const sorted: number[] = []; // current window values, ascending
  // First index k with sorted[k] >= v — the insertion point and, for a value
  // known to be present, the first of its duplicates.
  const lowerBound = (v: number): number => {
    let lo = 0;
    let hi = sorted.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (sorted[mid] < v) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  };
  let a = 0;
  let b = -1;
  for (let i = 0; i < n; i++) {
    while (b < n - 1 && t[b + 1] - t[i] <= halfWindowMs) {
      b++;
      sorted.splice(lowerBound(orig[b]), 0, orig[b]);
    }
    while (a < i && t[i] - t[a] > halfWindowMs) {
      sorted.splice(lowerBound(orig[a]), 1);
      a++;
    }
    values[i] = sorted[sorted.length >> 1];
  }
}

function rangeOf(arr: Float64Array): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < arr.length; i++) {
    const v = arr[i];
    if (Number.isNaN(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (min === Infinity) return { min: 0, max: 0 };
  return { min, max };
}
